import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Miniheader from '../../../../components/layout/MiniHeader/index'
import Tabs from '../../../../components/common/Tabs/Tabs'
import GridTable from '../../../../components/common/GridTable/GridTable'
import { ListItemButton, ListItemAvatar, Avatar } from '@mui/material'
import Typography from '../../../../components/common/Typography'
import Pagination from '../../../../components/common/Pagination/Pagination'
import Button from '../../../../components/common/Button'
import { getAllReports } from '../../../../features/reports/services/reportsApi.js'

const tabs = [
  { id: 'new', label: 'New' },
  { id: 'responded', label: 'Responded' },
  { id: 'not-responded', label: 'Not Responded' },
]

const columns = [
  { colname: 'Reporter Name', colSpan: 'col-span-2' },
  { colname: 'Report', colSpan: 'col-span-2' },
  { colname: 'Description', colSpan: 'col-span-2' },
  { colname: 'Time', colStyle: 'flex justify-end' },
]

const ReportList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { allReports, loading, error } = useSelector(state => state.reports)
  
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [activeTab, setActiveTab] = useState('new')
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  // Fetch reports on component mount
  useEffect(() => {
    dispatch(getAllReports())
  }, [dispatch])

  // Get reports array from different possible data structures
  const getReportsArray = () => {
    if (allReports?.reports) {
      return allReports.reports
    } else if (Array.isArray(allReports)) {
      return allReports
    } else if (allReports?.data?.reports) {
      return allReports.data.reports
    }
    return []
  }

  const reportsArray = getReportsArray()

  // Filter reports by date range
  const filterReportsByDate = (reportList, startDate, endDate) => {
    if (!startDate || !endDate || !reportList || !Array.isArray(reportList)) {
      return reportList || []
    }

    return reportList.filter(report => {
      const reportDate = new Date(report.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return reportDate >= start && reportDate <= end
    })
  }

  // Filter reports based on active tab and date filter
  const getFilteredReports = () => {
    let result = reportsArray

    // Apply date filter first if actively filtering
    if (isFiltering && dateFilter) {
      result = filterReportsByDate(result, dateFilter.startDate, dateFilter.endDate)
    }

    // Apply tab filter
    switch (activeTab) {
      case 'new':
        return result.filter(report =>
          report.status === 'PENDING' && !report.isOpened && !report.isRead
        )
      case 'responded':
        return result.filter(report =>
          report.status === 'RESPONDED' || report.isResponded
        )
      case 'not-responded':
        return result.filter(report =>
          report.status === 'PENDING' && (report.isOpened || report.isRead) && !report.isResponded
        )
      default:
        return result
    }
  }

  const filteredReports = getFilteredReports()

  // Get count for specific tab with current filters
  const getTabCount = (tabId) => {
    let result = reportsArray

    // Apply date filter if active
    if (isFiltering && dateFilter) {
      result = filterReportsByDate(result, dateFilter.startDate, dateFilter.endDate)
    }

    // Apply tab-specific filter
    switch (tabId) {
      case 'new':
        return result.filter(report =>
          report.status === 'PENDING' && !report.isOpened && !report.isRead
        ).length
      case 'responded':
        return result.filter(report =>
          report.status === 'RESPONDED' || report.isResponded
        ).length
      case 'not-responded':
        return result.filter(report =>
          report.status === 'PENDING' && (report.isOpened || report.isRead) && !report.isResponded
        ).length
      default:
        return result.length
    }
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      setCurrentPage(1)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Pagination logic
  const totalItems = filteredReports.length
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentReports = filteredReports.slice(startIndex, endIndex)

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(getAllReports())
  }

  const handleReportClick = (reportId) => {
    navigate(`/reports/${reportId}`)
  }

  // Helper functions
  const getReporterInfo = (reporterId) => {
    if (!reporterId) return { name: 'Anonymous', email: 'No email provided' }
    
    return {
      name: reporterId.full_name || 
            (reporterId.firstName && reporterId.lastName 
              ? `${reporterId.firstName} ${reporterId.lastName}` 
              : reporterId.email) || 'Anonymous',
      email: reporterId.email || 'No email provided'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getReportTypeLabel = (report) => {
    const typeLabels = {
      'MESSAGE_REPORT': 'Message Report',
      'USER_REPORT': 'User Report',
      'PROPERTY_REPORT': 'Property Report',
      'OTHER_REPORT': 'Other Report'
    }
    
    return typeLabels[report.reportType] || report.reportType || report.reason || 'Report'
  }

  const truncateText = (text, maxLength = 80) => {
    if (!text) return 'No description provided'
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  // const getReportStatus = (report) => {
  //   if (report.status === 'RESPONDED' || report.isResponded) {
  //     return { label: 'Responded', className: 'bg-green-100 text-green-800' }
  //   } else if (report.isRead) {
  //     return { label: 'Read', className: 'bg-blue-100 text-blue-800' }
  //   } else if (report.isOpened) {
  //     return { label: 'Opened', className: 'bg-yellow-100 text-yellow-800' }
  //   } else {
  //     return { label: 'New', className: 'bg-red-100 text-red-800' }
  //   }
  // }

  // Loading state
  if (loading && reportsArray.length === 0) {
    return (
      <>
        <Miniheader title="Reports" />
        <div className="ml-[250px] p-8 max-w-7xl">
          <div className="flex items-center justify-center py-12">
            <Typography>Loading reports...</Typography>
          </div>
        </div>
      </>
    )
  }

  // Error state
  if (error) {
    return (
      <>
        <Miniheader title="Reports" />
        <div className="ml-[250px] p-8 max-w-7xl">
          <div className="flex flex-col items-center justify-center py-12">
            <Typography className="text-red-500 mb-4">Error: {error}</Typography>
            <Button onClick={() => dispatch(getAllReports())}>
              Retry Loading Reports
            </Button>
          </div>
        </div>
      </>
    )
  }

  const renderReportRow = (report) => {
    const reporterInfo = getReporterInfo(report.reporterId)
    //const status = getReportStatus(report)
    
    return (
      <ListItemButton 
        key={report._id}
        component="div" 
        className="col-span-7 hover:bg-gray-50 cursor-pointer"
        onClick={() => handleReportClick(report._id)}
      >
        <div className="grid grid-cols-7 gap-6 w-full">
          {/* Reporter Name Column */}
          <div className="col-span-2">
            <div className="flex gap-3">
              <ListItemAvatar>
                <Avatar src={report.reporterId?.avatar || ''} />
              </ListItemAvatar>
              <div className="flex flex-col">
                <div className="flex gap-3 items-center">
                  <Typography className="font-medium">
                    {reporterInfo.name}
                  </Typography>
                  <Typography className="text-xs text-gray-500">
                    {report.reporterModel || 'Unknown'}
                  </Typography>
                </div>
                <Typography className="text-sm text-gray-600">
                  {reporterInfo.email}
                </Typography>
              </div>
            </div>
          </div>

          {/* Report Column */}
          <div className="flex items-center col-span-2 h-full">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Typography className="font-medium">
                  {getReportTypeLabel(report)}
                </Typography>
                
              </div>
              <Typography className="text-sm text-gray-600">
                {report.reason || 'No reason specified'}
              </Typography>
            </div>
          </div>

          {/* Description Column */}
          <div className="flex items-center col-span-2 h-full">
            <Typography className="text-sm">
              {truncateText(report.description)}
            </Typography>
          </div>

          {/* Time Column */}
          <div className="flex items-center col-span-1 h-full justify-end">
            <div className="text-right">
              <Typography className="text-sm">
                {formatDate(report.createdAt)}
              </Typography>
              <Typography className="text-xs text-gray-500">
                {new Date(report.createdAt).toLocaleDateString()}
              </Typography>
            </div>
          </div>
        </div>
      </ListItemButton>
    )
  }

  const renderEmptyState = (tabLabel) => (
    <div className="col-span-7 py-12 text-center">
      <Typography className="text-gray-500 text-lg mb-2">
        No {tabLabel.toLowerCase()} reports found
      </Typography>
      <Typography className="text-gray-400 text-sm mb-4">
        {tabLabel === 'New' && 'New reports will appear here when they are submitted.'}
        {tabLabel === 'Responded' && 'Reports you have responded to will appear here.'}
        {tabLabel === 'Not Responded' && 'Reports that have been opened but not responded to will appear here.'}
      </Typography>
      <Button variant="secondary" onClick={handleRefresh}>
        Refresh Reports
      </Button>
    </div>
  )

  return (
    <>
      <Miniheader
        title="Reports"
        data={reportsArray}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] p-8 max-w-7xl">
        <div className="flex py-3 justify-between">
          <Tabs
            tabs={tabs.map(tab => ({
              ...tab,
              label: `${tab.label} (${getTabCount(tab.id)})`
            }))}
            activeTab={activeTab}
            onTabChange={(newTab) => {
              setActiveTab(newTab)
              setCurrentPage(1) // Reset to first page when changing tabs
            }}
            justify="start"
          />
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onRefresh={handleRefresh}
            justify="end"
          />
        </div>

        {/* New Reports Tab */}
        {activeTab === 'new' && (
          <GridTable columns={columns} gridCols="grid-cols-7">
            {currentReports.length > 0 ? (
              currentReports.map(renderReportRow)
            ) : (
              renderEmptyState('New')
            )}
          </GridTable>
        )}

        {/* Responded Reports Tab */}
        {activeTab === 'responded' && (
          <GridTable columns={columns} gridCols="grid-cols-7">
            {currentReports.length > 0 ? (
              currentReports.map(renderReportRow)
            ) : (
              renderEmptyState('Responded')
            )}
          </GridTable>
        )}

        {/* Not Responded Reports Tab */}
        {activeTab === 'not-responded' && (
          <GridTable columns={columns} gridCols="grid-cols-7">
            {currentReports.length > 0 ? (
              currentReports.map(renderReportRow)
            ) : (
              renderEmptyState('Not Responded')
            )}
          </GridTable>
        )}

        
      </div> 
    </>
  )
}

export default ReportList